import { motion } from "framer-motion";
import { ChartSegment } from "types/reportTypes";
import { useState, useEffect } from "react";

interface Props {
  segment: ChartSegment;
  index: number;
  total: number;
  center: number;
  outerRadius: number;
  innerRadius: number;
  isHovered?: boolean;
  onHover?: () => void;
  onLeave?: () => void;
  onClick?: () => void;
  totalValue?: number;
  showPercentage?: boolean;
  showValue?: boolean;
  delay?: number;
  pdfMode?: boolean;
}

const getPointWithGap = (
  cx: number,
  cy: number,
  radius: number,
  angle: number,
  gapDistance: number,
  isOuter: boolean = true
) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  const x = cx + radius * Math.cos(rad);
  const y = cy + radius * Math.sin(rad);

  const tangentX = -Math.sin(rad);
  const tangentY = Math.cos(rad);
  const sign = isOuter ? 1 : -1;

  return {
    x: x + tangentX * gapDistance * sign,
    y: y + tangentY * gapDistance * sign,
  };
};

const createSegmentPath = (
  startAngle: number,
  endAngle: number,
  innerRadius: number,
  outerRadius: number,
  center: number,
  gapSize: number = 0
) => {
  const cx = center;
  const cy = center;
  const gap = gapSize / 2;

  const startOuter = getPointWithGap(
    cx,
    cy,
    outerRadius,
    startAngle,
    gap,
    true
  );
  const endOuter = getPointWithGap(cx, cy, outerRadius, endAngle, gap, false);
  const startInner = getPointWithGap(cx, cy, innerRadius, endAngle, gap, false);
  const endInner = getPointWithGap(cx, cy, innerRadius, startAngle, gap, true);

  const outerRadiusWithGap = Math.sqrt(
    Math.pow(startOuter.x - cx, 2) + Math.pow(startOuter.y - cy, 2)
  );
  const innerRadiusWithGap = Math.sqrt(
    Math.pow(startInner.x - cx, 2) + Math.pow(startInner.y - cy, 2)
  );

  const largeArc = endAngle - startAngle <= 180 ? "0" : "1";

  return `
    M ${startOuter.x} ${startOuter.y}
    A ${outerRadiusWithGap} ${outerRadiusWithGap} 0 ${largeArc} 1 ${endOuter.x} ${endOuter.y}
    L ${startInner.x} ${startInner.y}
    A ${innerRadiusWithGap} ${innerRadiusWithGap} 0 ${largeArc} 0 ${endInner.x} ${endInner.y}
    Z
  `;
};

const getTextPosition = (angle: number, radius: number, center: number) => {
  const cx = center;
  const cy = center;
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
};

const wrapText = (
  text: string,
  maxWidth: number,
  fontSize: number
): string[] => {
  if (!text) return [""];

  const charWidth = fontSize * 0.55;
  const maxChars = Math.max(1, Math.floor(maxWidth / charWidth));

  if (text.length <= maxChars) return [text];

  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  if (words.length === 1) {
    const word = words[0];
    if (word.length > maxChars) {
      for (let i = 0; i < word.length; i += maxChars) {
        lines.push(word.substring(i, Math.min(i + maxChars, word.length)));
      }
      return lines;
    }
    return [word];
  }

  words.forEach((word) => {
    if (word.length > maxChars) {
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = "";
      }
      for (let i = 0; i < word.length; i += maxChars) {
        const chunk = word.substring(i, Math.min(i + maxChars, word.length));
        if (chunk) lines.push(chunk);
      }
      return;
    }

    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= maxChars) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine.trim());
      currentLine = word;
    }
  });

  if (currentLine) lines.push(currentLine.trim());

  return lines.length > 0 ? lines : [text];
};

const calculateSizes = (
  segmentWidth: number,
  arcWidth: number,
  isMobile: boolean,
  totalSegments: number,
  pdfMode: boolean
) => {
  let baseIconSize: number;
  let baseTitleSize: number;
  let baseValueSize: number;
  let baseSpacing: number;

  // گام ۱: سایز پایه — دو مسیر جدا
  if (isMobile) {
    baseIconSize = Math.min(segmentWidth * 0.2, 24);
    baseTitleSize = Math.min(segmentWidth * 0.1, 11);
    baseValueSize = Math.min(segmentWidth * 0.13, 14);
    baseSpacing = Math.min(segmentWidth * 0.25, 22);
  } else if (pdfMode) {
    baseIconSize = Math.min(segmentWidth * 0.22, 28);
    baseTitleSize = Math.min(segmentWidth * 0.11, 12);
    baseValueSize = Math.min(segmentWidth * 0.15, 16);
    baseSpacing = Math.min(segmentWidth * 0.35, 38);
  } else {
    baseIconSize = Math.min(segmentWidth * 0.25, 34);
    baseTitleSize = Math.min(segmentWidth * 0.22, 20);
    baseValueSize = Math.min(segmentWidth * 0.12, 22);
    baseSpacing = Math.min(segmentWidth * 0.4, 44);
  }

  // گام ۲: محدودیت arcWidth — فقط PDF
  // ============================================================
  if (pdfMode) {
    baseTitleSize = Math.min(baseTitleSize, arcWidth / 4.5);
    baseValueSize = Math.min(baseValueSize, arcWidth / 5);
    baseIconSize = Math.min(baseIconSize, arcWidth * 0.35);
    baseSpacing = Math.min(baseSpacing, arcWidth * 0.9);
  }

  // گام ۳: کاهش برای تعداد زیاد سگمنت
  let sizeMultiplier = 1;
  if (totalSegments > 6) {
    if (pdfMode) {
      sizeMultiplier = Math.max(0.4, 1 - (totalSegments - 6) * 0.07);
    } else {
      sizeMultiplier = Math.max(0.85, 1 - (totalSegments - 6) * 0.02);
    }
  }

  return {
    iconSize: baseIconSize * sizeMultiplier,
    titleSize: baseTitleSize * sizeMultiplier,
    valueSize: baseValueSize * sizeMultiplier,
    spacing: baseSpacing * sizeMultiplier,
  };
};

export default function Segment({
  segment,
  index,
  total,
  center,
  outerRadius,
  innerRadius,
  isHovered = false,
  onHover,
  onLeave,
  onClick,
  totalValue = 0,
  showPercentage = true,
  delay = 0,
  pdfMode = false,
}: Props) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // گام ۱: زوایا
  const anglePerSegment = 360 / total;
  const gapSize = Math.min(outerRadius * 0.03, 10);
  const midRadius = (innerRadius + outerRadius) / 2;
  const gapAngle = (gapSize / midRadius) * (180 / Math.PI) * 1.2;

  const startAngle = index * anglePerSegment + gapAngle / 2;
  const endAngle = (index + 1) * anglePerSegment - gapAngle / 2;
  const middleAngle = (startAngle + endAngle) / 2;

  // گام ۲: شعاع‌ها
  const effectiveOuterRadius = outerRadius - gapSize * 0.3;
  const effectiveInnerRadius = innerRadius + gapSize * 0.3;
  const segmentWidth = effectiveOuterRadius - effectiveInnerRadius;

  // گام ۳: طول قوس
  const centerRadius = effectiveInnerRadius + segmentWidth / 2;
  const arcWidth = ((anglePerSegment * Math.PI) / 180) * centerRadius;

  // گام ۴: محاسبه‌ی سایزها
  const sizes = calculateSizes(
    segmentWidth,
    arcWidth,
    isMobile,
    total,
    pdfMode
  );
  const { iconSize, titleSize, valueSize, spacing } = sizes;

  const centerPos = getTextPosition(middleAngle, centerRadius, center);

  // گام ۵: شکستن متن
  const maxTextWidth = arcWidth * 0.85;
  const textLines = wrapText(segment.title, maxTextWidth, titleSize);

  // گام ۶: تصمیم نمایش — دسکتاپ آزادتر، PDF سخت‌گیرانه‌تر
  const showIcon = pdfMode
    ? iconSize > 7 && segmentWidth > 18 && total <= 10
    : iconSize > 5 && segmentWidth > 12;

  const showTitle = pdfMode
    ? titleSize > 5 && arcWidth > 14 && textLines.length > 0
    : titleSize > 4 && arcWidth > 8 && textLines.length > 0;

  const showValue = pdfMode
    ? valueSize > 6 && arcWidth > 16
    : valueSize > 5 && arcWidth > 10;

  const hasAnyContent = showIcon || showTitle || showValue;

  // گام ۷: چیدمان عمودی
  const lineHeight = titleSize * 1.25;
  const totalTextHeight = textLines.length * lineHeight;

  const iconBlock = showIcon ? iconSize + spacing * 0.3 : 0;
  const valueBlock = showValue ? spacing * 0.3 + valueSize : 0;
  const contentHeight = iconBlock + totalTextHeight + valueBlock;

  const contentTop = centerPos.y - contentHeight / 2;

  let cursorY = contentTop;

  const iconCenterY = cursorY + iconSize / 2;
  if (showIcon) cursorY += iconSize + spacing * 0.3;

  const titleStartY = cursorY + lineHeight * 0.5;
  cursorY += totalTextHeight + spacing * 0.3;

  const valueCenterY = cursorY + valueSize / 2;

  const Icon = segment.icon;

  const percentage =
    totalValue === 100
      ? segment.value
      : totalValue > 0
      ? (segment.value / totalValue) * 100
      : 0;

  const formattedValue = showValue ? segment.value.toLocaleString() : "";
  const formattedPercentage = percentage.toFixed(1);
  const neonColor = segment.color || "#00ff88";

  const pathData = createSegmentPath(
    startAngle,
    endAngle,
    innerRadius,
    outerRadius,
    center,
    gapSize
  );

  return (
    <g
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <defs>
        <radialGradient
          id={`neon-glow-gradient-${segment.id}`}
          cx="50%"
          cy="50%"
          r="70%"
        >
          <stop offset="0%" stopColor={neonColor} stopOpacity="0.02" />
          <stop offset="30%" stopColor={neonColor} stopOpacity="0.1" />
          <stop offset="60%" stopColor={neonColor} stopOpacity="0.4" />
          <stop offset="85%" stopColor={neonColor} stopOpacity="0.8" />
          <stop offset="100%" stopColor={neonColor} stopOpacity="1" />
        </radialGradient>

        <linearGradient
          id={`neon-border-${segment.id}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor={neonColor} stopOpacity="0.3" />
          <stop offset="30%" stopColor={neonColor} stopOpacity="0.5" />
          <stop offset="60%" stopColor="#ffffff" stopOpacity="0.4" />
          <stop offset="80%" stopColor={neonColor} stopOpacity="0.7" />
          <stop offset="100%" stopColor={neonColor} stopOpacity="0.9" />
        </linearGradient>

        <filter
          id={`neon-glow-${segment.id}`}
          x="-30%"
          y="-30%"
          width="160%"
          height="160%"
        >
          <feGaussianBlur
            in="SourceGraphic"
            stdDeviation="1.5"
            result="blur1"
          />
          <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur2" />
          <feMerge>
            <feMergeNode in="blur2" />
            <feMergeNode in="blur1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter
          id={`hover-glow-${segment.id}`}
          x="-40%"
          y="-40%"
          width="180%"
          height="180%"
        >
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur2" />
          <feMerge>
            <feMergeNode in="blur2" />
            <feMergeNode in="blur1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ============ بدنه‌ی سگمنت ============ */}
      <motion.path
        d={pathData}
        fill={`url(#neon-glow-gradient-${segment.id})`}
        initial={pdfMode ? false : { opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.3,
          delay: pdfMode ? 0 : delay,
          ease: "easeOut",
        }}
      />

      {/* ============ حاشیه‌ی نئونی ============ */}
      <motion.path
        d={pathData}
        fill="none"
        stroke={`url(#neon-border-${segment.id})`}
        strokeWidth={isHovered ? 4 : 3.5}
        filter={`url(${
          isHovered ? `#hover-glow-${segment.id}` : `#neon-glow-${segment.id}`
        })`}
        initial={pdfMode ? false : { opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0.8 }}
        transition={{ duration: 0.15 }}
      />

      {isHovered && (
        <motion.path
          d={createSegmentPath(
            startAngle + 0.5,
            endAngle - 0.5,
            innerRadius + 3,
            outerRadius - 3,
            center,
            gapSize
          )}
          fill="none"
          stroke={neonColor}
          strokeWidth="1"
          strokeOpacity="0.2"
          initial={pdfMode ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.1 }}
        />
      )}

      {/* ============ محتوا ============ */}
      {hasAnyContent && (
        <motion.g
          initial={pdfMode ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.3,
            delay: pdfMode ? 0 : delay + 0.1,
            ease: "easeOut",
          }}
        >
          {/* آیکون */}
          {showIcon && (
            <foreignObject
              x={centerPos.x - iconSize / 2}
              y={iconCenterY - iconSize / 2}
              width={iconSize}
              height={iconSize}
            >
              <div
                className="flex h-full w-full items-center justify-center rounded-full"
                style={{ background: "transparent" }}
              >
                <Icon
                  size={Math.min(iconSize * 0.9, iconSize)}
                  style={{
                    color: neonColor,
                    filter: `drop-shadow(0 0 15px ${neonColor}60)`,
                  }}
                />
              </div>
            </foreignObject>
          )}

          {/* متن — چند خطی، کامل */}
          {showTitle && (
            <g>
              {textLines.map((line, i) => (
                <text
                  key={i}
                  x={centerPos.x}
                  y={titleStartY + i * lineHeight}
                  fill="white"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontWeight="700"
                  fontSize={Math.max(titleSize, 4)}
                  style={{
                    textShadow: `0 0 10px ${neonColor}20`,
                    letterSpacing: "0.1px",
                  }}
                >
                  {line}
                </text>
              ))}
            </g>
          )}

          {/* مقدار */}
          {showValue && (
            <text
              x={centerPos.x}
              y={valueCenterY}
              fill="white"
              textAnchor="middle"
              dominantBaseline="middle"
              fontWeight="900"
              fontSize={Math.max(valueSize, 5)}
              style={{
                textShadow: `0 0 15px ${neonColor}30`,
              }}
            >
              {showPercentage ? `${formattedPercentage}%` : formattedValue}
            </text>
          )}
        </motion.g>
      )}
    </g>
  );
}
